import React, { useState, useEffect } from 'react';
import { fetchQuizQuestions } from '../api/studentApi';
import './QuizView.css';
import LoadingState from '../components/LoadingState';
import QuestionText from './QuestionText';
import OptionText from './OptionText';
import Modal from './Modal';
import ImagePreview from './ImagePreview';
import AnalogClock from './questions/AnalogClock';
import { playTapSound, playClickSound } from '../utils/audio';
import QuizProgress from './QuizProgress';
import QuizCheckpoint from './QuizCheckpoint';
import { useViewportWidth } from '../hooks/useViewportWidth';

const QuizView = ({ topicId, onComplete, topicName }) => {
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [responses, setResponses] = useState([]);
    const [loading, setLoading] = useState(true);

    // NEW: Track the currently selected option before hitting "Next"
    const [selectedOption, setSelectedOption] = useState(null);

    const [questionStartTime, setQuestionStartTime] = useState(Date.now());
    const [overallStartTime] = useState(Date.now());
    const [error, setError] = useState(null);

    const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);
    const [responseStatues, setResponseStatuses] = useState(['undefined']);

    const screenWidth = useViewportWidth();
    const isMobile = screenWidth <= 768;

    useEffect(() => {
        fetchQuestions();
    }, [topicId]);

    const fetchQuestions = async () => {
        try {
            const res = await fetchQuizQuestions(topicId);
            setQuestions(res.data);
            setLoading(false);
            setQuestionStartTime(Date.now());
        } catch (err) {
            setQuestions([]);
            setLoading(false);
            if (err.response) {
                const status = err.response.status;
                if (status === 404) {
                    setError('NOT_FOUND');
                } else if (status === 403) {
                    setError('FORBIDDEN');
                } else {
                    setError('SERVER_ERROR');
                }
            } else {
                setError('NETWORK_ERROR');
            }
        }
    };

    // User just clicks a bubble, but we don't move to the next question yet
    const handleOptionClick = (optionId, isCorrect) => {
        playTapSound(); // Play the tap sound on option click
        setSelectedOption({ optionId, isCorrect });
    };

    // This runs when the student clicks the "Next" or "Finish" button
    const handleNext = (selectedOptionValue) => {
        const timeSpent = (Date.now() - questionStartTime) / 1000;

        const newResponse = {
            questionId: questions[currentIndex].id,
            selectedOptionId: selectedOption ? selectedOption.optionId : null,
            timeSpent: timeSpent
        };

        const updatedResponses = [...responses, newResponse];
        setResponses(updatedResponses);

        let status = 'skipped';
        if (selectedOptionValue) {
            status = selectedOptionValue.isCorrect ? 'correct' : 'wrong';
        }

        setResponseStatuses(prev => ({
            ...prev,
            [currentIndex]: status
        }));

        if (currentIndex < questions.length - 1) {
            // Reset for the next question
            setCurrentIndex(currentIndex + 1);
            setSelectedOption(null);
            setQuestionStartTime(Date.now());
            playClickSound();
        } else {
            // Quiz Finished
            const totalTime = (Date.now() - overallStartTime) / 1000;
            onComplete(questions, updatedResponses, totalTime);
        } // Play the click sound when moving to the next question
    };

    const getErrorComponent = () => {
        let errorMessage = "";
        switch (error) {
            case 'NOT_FOUND':
                errorMessage = "This topic does not exist.";
                break;
            case 'FORBIDDEN':
                errorMessage = "This topic is out of your league.";
                break;
            default:
                errorMessage = "Something went wrong.";
                break;
        }
        return <LoadingState message={`${errorMessage}`} showLoader={false} />;
    };

    const onClickImage = (imageUrl) => {
        setPreviewImage(imageUrl);
        setImagePreviewOpen(true);
    };


    if (loading) return <LoadingState message={`Preparing your quiz...`} />;
    if (!error && !questions.length) return <LoadingState message={`No questions found for ${topicName}.`} showLoader={false} />;
    if (error) return getErrorComponent();

    const currentQuestion = questions[currentIndex];
    const clockConfig = currentQuestion?.metadata?.clockConfig;

    return (
        <div className="quiz-container">
            <Modal isOpen={imagePreviewOpen} onClose={() => setImagePreviewOpen(false)} title={"Preview"}>
                {previewImage && (
                    <ImagePreview
                        imageUrl={previewImage}
                        onCancel={() => setPreviewImage(null)}
                    />
                )}
            </Modal>
            <QuizProgress currentIndex={currentIndex} questionsCount={questions.length} />
            {!isMobile &&
                <QuizCheckpoint currentIndex={currentIndex} questionsCount={questions.length} statuses={responseStatues} />
            }

            <div key={currentIndex} className="question-part">
                {currentQuestion.imageUrl && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '100%',
                        backgroundColor: '#f3f4f6', /* Light grey tone (equivalent to Tailwind gray-100) */
                        padding: '16px',           /* Gives breathing room around the image */
                        borderRadius: '12px'       /* Smooths out the box container corners */
                    }} onClick={() => onClickImage(currentQuestion.imageUrl)}>
                        <img
                            src={currentQuestion.imageUrl}
                            alt="Question visual layout"
                            className="question-image"
                            style={{
                                maxHeight: '150px',
                                width: 'auto',
                                objectFit: 'contain',
                                borderRadius: '8px', /* Optional: Rounds the corners slightly so the shadow looks cleaner */
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)' /* Medium elegant shadow */
                            }}
                            loading="lazy"
                        />
                    </div>
                )}
                <h2 className="question-heading">
                    <QuestionText text={currentQuestion.text} />
                </h2>
                {clockConfig && (
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '24px 0' }}>
                        <AnalogClock
                            hour={clockConfig.hour}
                            minute={clockConfig.minute}
                            size={220}
                        />
                    </div>
                )}

                {/* Options Grid */}
                <div className="options-grid">
                    {currentQuestion.Options.map((option) => (
                        <button
                            key={option.id}
                            onClick={() => handleOptionClick(option.id, option.isCorrect)}
                            // If this option is selected, add the 'selected' class
                            className={`option-btn ${selectedOption?.optionId === option.id ? 'selected' : ''}`}
                        >
                            <span className="option-text">
                                <OptionText text={option.text} />
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            {/* NEW: Action Footer with Next/Finish Button */}
            <div className="action-footer">
                <button
                    onClick={() => {
                        setSelectedOption(null);
                        handleNext(null); // Skips by passing null
                    }}
                    disabled={selectedOption}
                    className={`skip-btn ${selectedOption !== null ? 'active' : 'normal'}`}
                >
                    Skip question
                </button>

                <button
                    onClick={() => {
                        handleNext(selectedOption);
                    }}
                    disabled={selectedOption === null}
                    className={`next-btn ${selectedOption !== null ? 'active' : ''}`}
                >
                    {currentIndex === questions.length - 1 ? 'Done' : 'Next'}
                </button>
            </div>
        </div>
    );
};

export default QuizView;